import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { CreditCard, Email, Person, Phone } from '@mui/icons-material'
import type { CardData, CreditCardData, InstallmentOption } from '../../types/cart'
import { paymentService } from '../../services/paymentService'

interface CreditCardFormProps {
  onSubmit: (cardData: CardData, creditCardData: CreditCardData) => void
  isProcessing: boolean
  totalAmount: number
}

const CreditCardForm: React.FC<CreditCardFormProps> = ({ onSubmit, isProcessing, totalAmount }) => {
  const [cardData, setCardData] = useState<CardData>({
    number: '',
    cvv: '',
    expirationMonth: '',
    expirationYear: '',
    holderName: '',
    holderDocument: '',
    brand: ''
  })

  const [creditCardData, setCreditCardData] = useState<CreditCardData>({
    customerName: '',
    cpf: '',
    email: '',
    phoneNumber: '',
    installments: 1
  })

  const [installmentOptions, setInstallmentOptions] = useState<InstallmentOption[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [loadingInstallments, setLoadingInstallments] = useState(false)

  useEffect(() => {
    const run = async () => {
      const clean = cardData.number.replace(/\D/g, '')
      if (clean.length < 6) return
      const brand = await paymentService.identifyCardBrand(cardData.number)
      setCardData((prev) => ({ ...prev, brand }))

      if (brand !== 'unknown') {
        setLoadingInstallments(true)
        try {
          const options = await paymentService.getInstallmentOptions(totalAmount, brand)
          setInstallmentOptions(options)
          setCreditCardData((prev) => ({ ...prev, installments: 1 }))
        } catch {
          setInstallmentOptions([])
        } finally {
          setLoadingInstallments(false)
        }
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardData.number, totalAmount])

  const formatCPF = (cpf: string) =>
    cpf
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')

  const formatPhone = (phone: string) =>
    phone
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(\d{4})-(\d)(\d{4})/, '$1$2-$3')
      .replace(/(-\d{4})\d+?$/, '$1')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const validation = paymentService.validateCardData(cardData)
    const nextErrors: string[] = []
    if (!validation.isValid) nextErrors.push(...validation.errors)

    if (!creditCardData.customerName.trim()) nextErrors.push('Nome do cliente é obrigatório')
    if (creditCardData.cpf.replace(/\D/g, '').length !== 11) nextErrors.push('CPF deve ter 11 dígitos')
    if (!creditCardData.email.includes('@')) nextErrors.push('Email válido é obrigatório')
    if (creditCardData.phoneNumber.replace(/\D/g, '').length < 10) nextErrors.push('Telefone inválido')

    if (nextErrors.length) {
      setErrors(nextErrors)
      return
    }

    onSubmit(cardData, creditCardData)
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
            Dados do cliente
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nome completo"
            value={creditCardData.customerName}
            onChange={(e) => setCreditCardData((p) => ({ ...p, customerName: e.target.value }))}
            InputProps={{ startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} /> }}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="CPF"
            value={creditCardData.cpf}
            onChange={(e) => setCreditCardData((p) => ({ ...p, cpf: formatCPF(e.target.value) }))}
            inputProps={{ maxLength: 14 }}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={creditCardData.email}
            onChange={(e) => setCreditCardData((p) => ({ ...p, email: e.target.value }))}
            InputProps={{ startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} /> }}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Telefone"
            value={creditCardData.phoneNumber}
            onChange={(e) => setCreditCardData((p) => ({ ...p, phoneNumber: formatPhone(e.target.value) }))}
            inputProps={{ maxLength: 15 }}
            InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} /> }}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 1, mt: 1 }}>
            Dados do cartão
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Número do cartão"
            value={cardData.number}
            onChange={(e) => setCardData((p) => ({ ...p, number: paymentService.formatCardNumber(e.target.value) }))}
            inputProps={{ maxLength: 19 }}
            InputProps={{ startAdornment: <CreditCard sx={{ mr: 1, color: 'text.secondary' }} /> }}
            required
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="CVV"
            value={cardData.cvv}
            onChange={(e) => setCardData((p) => ({ ...p, cvv: paymentService.formatCVV(e.target.value) }))}
            inputProps={{ maxLength: 4 }}
            required
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            fullWidth
            label="Mês"
            value={cardData.expirationMonth}
            onChange={(e) => setCardData((p) => ({ ...p, expirationMonth: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
            inputProps={{ maxLength: 2 }}
            required
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            fullWidth
            label="Ano"
            value={cardData.expirationYear}
            onChange={(e) => setCardData((p) => ({ ...p, expirationYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
            inputProps={{ maxLength: 4 }}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome no cartão"
            value={cardData.holderName}
            onChange={(e) => setCardData((p) => ({ ...p, holderName: e.target.value }))}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Parcelas</InputLabel>
            <Select
              label="Parcelas"
              value={creditCardData.installments}
              onChange={(e) => setCreditCardData((p) => ({ ...p, installments: Number(e.target.value) }))}
              disabled={loadingInstallments || installmentOptions.length === 0}
            >
              {(installmentOptions.length ? installmentOptions : [{ installment: 1, value: totalAmount, currency: 'BRL', has_interest: false }]).map(
                (opt) => (
                  <MenuItem key={opt.installment} value={opt.installment}>
                    {opt.installment}x de {formatCurrency(opt.value)} {opt.has_interest ? '(com juros)' : ''}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          {loadingInstallments && (
            <Typography variant="caption" color="text.secondary">
              Buscando opções de parcelamento…
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" size="large" disabled={isProcessing} sx={{ px: 3, py: 1.5 }}>
              {isProcessing ? <CircularProgress size={22} color="inherit" /> : 'Pagar com cartão'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default CreditCardForm

